/* eslint-disable import/no-cycle */
import TaskService from '@/services/api/task';
import Vue from 'vue';

const getDefaultState = () => ({
  isLoadingTask: false,
  serviceOrderTaskList: [],
  serviceOrderTaskIntervalActive: null,
  taskToShowIntervalsHistory: null,
  taskIntervalActiveTotalTime: 0,
  intervalTimer: null,
  taskToShowMedias: null
});

const state = getDefaultState();

const getters = {
  getTaskIntervalActiveTotalTime(state) {
    return state.taskIntervalActiveTotalTime;
  },
  isLoadingTask(state) {
    return state.isLoadingTask;
  },
  getServiceOrderTaskList(state) {
    return state.serviceOrderTaskList;
  },
  getServiceOrderTaskIntervalActive(state) {
    return state.serviceOrderTaskIntervalActive;
  },
  getTaskToShowIntervalsHistory(state) {
    return state.taskToShowIntervalsHistory;
  },
  getTaskToShowMedias(state) {
    return state.taskToShowMedias;
  }
};

const mutations = {
  SET_TASK_INTERVAL_ACTIVE_TOTAL_TIME(state, totalTime) {
    state.taskIntervalActiveTotalTime = totalTime;
  },
  SET_INTERVAL_TIMER(state, timer) {
    state.intervalTimer = timer;
  },
  CLEAR_INTERVAL_TIMER(state) {
    if (state.intervalTimer) {
      clearInterval(state.intervalTimer);
      state.intervalTimer = null;
      state.taskIntervalActiveTotalTime = 0;
    }
  },
  SET_LOADING(state, payload) {
    state.isLoadingTask = payload;
  },
  SET_SERVICE_ORDER_TASK_LIST(state, payload) {
    state.serviceOrderTaskList = payload;
  },
  SET_SERVICE_ORDER_TASK_INTERVAL_ACTIVE(state, { taskId, taskIntervalId }) {
    const task = state.serviceOrderTaskList.find((task) => task.id === taskId);

    if (!task) {
      return;
    }

    const intervalActive = task.task_intervals.find((interval) => interval.id === taskIntervalId);

    if (intervalActive) {
      state.serviceOrderTaskIntervalActive = intervalActive;
    } else {
      state.serviceOrderTaskIntervalActive = null;
    }
  },
  SET_TASK_STATUS(state, { taskId, status }) {
    const task = state.serviceOrderTaskList.find((task) => task.id === taskId);
    if (task) {
      task.status = status;
    }
  },
  SET_TASK(state, { task }) {
    const taskIndex = state.serviceOrderTaskList.findIndex((t) => t.id === task.id);
    if (taskIndex !== -1) {
      Vue.set(state.serviceOrderTaskList, taskIndex, task);
    }
  },
  SET_INTERVAL(state, { task }) {
    const taskIndex = state.serviceOrderTaskList.findIndex((taskOld) => taskOld.id === task.id);
    if (taskIndex !== -1) {
      Vue.set(state.serviceOrderTaskList[taskIndex], 'task_intervals', task.task_intervals);
    }
  },
  SET_TASK_TO_SHOW_INTERVAL_HISTORY(state, payload) {
    Vue.set(state, 'taskToShowIntervalsHistory', payload);
  },
  SET_TASK_TO_SHOW_MEDIAS(state, payload) {
    Vue.set(state, 'taskToShowMedias', payload);
  },
  UPDATE_TASK_TO_SHOW_INTERVAL_HISTORY(state, payload) {
    const taskIndex = state.serviceOrderTaskList.findIndex((task) => task.id === payload.taskId);
    if (taskIndex !== -1) {
      Vue.set(state.serviceOrderTaskList[taskIndex], 'task_intervals', payload.task_intervals);
    }
  },
  DELETE_TASK(state, taskId) {
    state.serviceOrderTaskList = state.serviceOrderTaskList.filter((task) => task.id !== taskId);
  },
  DELETE_TASK_INTERVAL(state, { intervalId }) {
    state.taskToShowIntervalsHistory.task_intervals = state.taskToShowIntervalsHistory.task_intervals.filter(
      (interval) => interval.id !== intervalId
    );
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState());
  }
};

const actions = {
  resetState({ commit }) {
    commit('RESET_STATE');
  },
  startIntervalTimer({ commit, state }) {
    commit('CLEAR_INTERVAL_TIMER');

    if (state.serviceOrderTaskIntervalActive) {
      const startDate = new Date(state.serviceOrderTaskIntervalActive.start_date);
      const currentTime = new Date();
      const totalMilliseconds = currentTime - startDate;
      commit('SET_TASK_INTERVAL_ACTIVE_TOTAL_TIME', totalMilliseconds);

      const timer = setInterval(() => {
        const startDate = new Date(state.serviceOrderTaskIntervalActive.start_date);
        const currentTime = new Date();
        const totalMilliseconds = currentTime - startDate;
        commit('SET_TASK_INTERVAL_ACTIVE_TOTAL_TIME', totalMilliseconds);
      }, 60000);

      commit('SET_INTERVAL_TIMER', timer);
    }
  },
  stopIntervalTimer({ commit }) {
    commit('CLEAR_INTERVAL_TIMER');
  },
  setLoading({ commit }, payload) {
    commit('SET_LOADING', payload);
  },
  setServiceOrderTaskList({ commit }, payload) {
    commit('SET_SERVICE_ORDER_TASK_LIST', payload);
  },
  setServiceOrderTaskIntervalActive({ commit }, payload) {
    commit('SET_SERVICE_ORDER_TASK_INTERVAL_ACTIVE', payload);
  },
  setTask({ commit }, { task }) {
    commit('SET_TASK', { task });
  },
  setInterval({ commit }, { task }) {
    commit('SET_INTERVAL', { task });
  },
  setTaskToShowIntervalsHistory({ commit }, payload) {
    commit('SET_TASK_TO_SHOW_INTERVAL_HISTORY', payload);
  },
  setTaskToShowMedias({ commit }, payload) {
    commit('SET_TASK_TO_SHOW_MEDIAS', payload);
  },

  async getTaskById({ commit, dispatch }, taskId) {
    try {
      dispatch('setLoading', true);
      const response = await TaskService.getById(taskId);
      commit('SET_TASK', { task: response.data });
    } catch {
      throw Error('Erro ao buscar tarefa');
    } finally {
      dispatch('setLoading', false);
    }
  },

  async getServiceOrderTaskList({ commit, dispatch }, serviceOrderId) {
    if (!serviceOrderId) return;
    try {
      dispatch('setLoading', true);

      const response = await TaskService.getAll(serviceOrderId);

      commit('SET_SERVICE_ORDER_TASK_LIST', response.data);

      const workingTask = response.data.find((task) => task.status === 'working');
      if (workingTask) {
        const activeInterval = workingTask.task_intervals.find((interval) => !interval.end_date);
        if (activeInterval) {
          commit('SET_SERVICE_ORDER_TASK_INTERVAL_ACTIVE', { taskId: workingTask.id, taskIntervalId: activeInterval.id });
          dispatch('startIntervalTimer');
        }
      }
    } catch {
      throw Error('Erro ao buscar tarefas');
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async createTask({ dispatch }, infoToCreate) {
    try {
      dispatch('setLoading', true);

      await TaskService.create(infoToCreate.serviceOrderId, infoToCreate.body);
      await dispatch('getServiceOrderTaskList', infoToCreate.serviceOrderId);
    } catch (e) {
      console.log(e);
      throw Error('Erro ao buscar tarefas');
    }
  },
  async updateTask({ dispatch }, { taskId, body }) {
    try {
      dispatch('setLoading', true);
      await TaskService.updateTask(taskId, body);
      await dispatch('getTaskById', taskId);
    } catch (e) {
      console.log(e);
      throw Error('Erro ao atualizar tarefa');
    } finally {
      dispatch('setLoading', false);
    }
  },
  async deleteTask({ dispatch, commit }, taskId) {
    try {
      dispatch('setLoading', true);

      await TaskService.delete(taskId);
      commit('DELETE_TASK', taskId);
    } catch (error) {
      console.log(error);
    } finally {
      dispatch('setLoading', false);
    }
  },
  async createTaskInterval({ dispatch, commit }, infoToCreate) {
    try {
      dispatch('setLoading', true);

      const response = await TaskService.createTaskInterval(infoToCreate.taskId, infoToCreate.body);

      await dispatch('getTaskById', infoToCreate.taskId);
      commit('SET_SERVICE_ORDER_TASK_INTERVAL_ACTIVE', { taskId: infoToCreate.taskId, taskIntervalId: response.data.created_id });
      dispatch('startIntervalTimer');
    } catch (e) {
      console.log(e);
      throw Error('Erro ao criar intervalo');
    } finally {
      dispatch('setLoading', false);
    }
  },
  async updateTaskInterval({ commit, dispatch }, { taskId, intervalId, body }) {
    try {
      dispatch('setLoading', true);

      const response = await TaskService.updateTaskInterval(taskId, intervalId, body);

      commit('SET_TASK', { task: response.data });
      commit('UPDATE_TASK_TO_SHOW_INTERVAL_HISTORY', { taskId });
    } catch (e) {
      console.log(e);
      throw Error('Erro ao atualizar intervalo');
    } finally {
      dispatch('setLoading', false);
    }
  },

  async finishTaskInterval({ dispatch }, { taskId, intervalId, body }) {
    try {
      dispatch('setLoading', true);

      await TaskService.updateTask(taskId, { status: 'paused' });
      await TaskService.updateTaskInterval(taskId, intervalId, body);
      await dispatch('getTaskById', taskId);

      dispatch('setServiceOrderTaskIntervalActive', { taskId, taskIntervalId: null });
      dispatch('stopIntervalTimer');
    } catch (e) {
      console.log(e);
      throw Error('Erro ao atualizar tarefa');
    } finally {
      dispatch('setLoading', false);
    }
  },

  async deleteTaskInterval({ dispatch, commit }, { taskId, intervalId }) {
    try {
      dispatch('setLoading', true);

      await TaskService.deleteTaskInterval(taskId, intervalId);
      commit('DELETE_TASK_INTERVAL', { intervalId });
    } catch (error) {
      console.log(error);
    } finally {
      dispatch('setLoading', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
